import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography, Alert, useTheme, IconButton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { NavLink } from 'react-router-dom';
import type {
  ActionDescriptor,
  AuthoritativeFinancialResultMetadata,
  DashboardDescriptor,
  DashboardWidgetDescriptor,
  TableDescriptor,
  TranslationKey,
} from '@finance/contracts';
import { DASHBOARD_HOLDINGS_TABLE, getDashboardTableDescriptor } from '@finance/contracts';
import { resolveTKey } from '../../i18n/resolve-key';
import { useLocalizedFormatter } from '../../runtime/formatting/use-localized-formatter';
import { MetricCard } from '../data-display/MetricCard';
import { SurfaceCard } from '../data-display/SurfaceCard';
import { FinancialDataQualityNotice } from '../financial/FinancialDataQualityNotice';
import { FinancialVisualizationRenderer } from '../financial/FinancialVisualizationRenderer';
import { TableRenderer } from './TableRenderer.tsx';

export interface DashboardWidgetResult {
  id: string;
  value: string | null;
  data: unknown;
  state: 'CURRENT' | 'STALE' | 'PARTIAL' | 'EMPTY' | 'UNAVAILABLE' | string;
}

export interface DashboardRendererProps {
  contract: DashboardDescriptor;
  widgetResults?: DashboardWidgetResult[];
  sourceAsOf?: string | null;
  asOfDate?: string;
  state?: string;
  partialData?: boolean;
  authoritativeMetadata?: AuthoritativeFinancialResultMetadata;
  tableData?: Record<string, Record<string, unknown>[]>;
  rowActivationActions?: Record<string, ActionDescriptor | undefined>;
  drilldownPaths?: Record<string, string | undefined>;
  showTitle?: boolean;
}

function metricCurrency(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined;
  const currency = (data as Record<string, unknown>).currency;
  return typeof currency === 'string' && /^[A-Z]{3}$/.test(currency) ? currency : undefined;
}

function dataQualitySeverity(state: string | undefined): 'success' | 'warning' | 'error' | 'info' {
  if (state === 'CURRENT') return 'success';
  if (state === 'STALE' || state === 'PARTIAL') return 'warning';
  if (state === 'UNAVAILABLE') return 'error';
  return 'info';
}

function WidgetRenderer({
  widget,
  result,
  sourceAsOf,
  tableData,
  rowActivationAction,
  drilldownPath,
  emptyStateKey,
}: {
  widget: DashboardWidgetDescriptor;
  result?: DashboardWidgetResult;
  sourceAsOf?: string;
  tableData?: Record<string, Record<string, unknown>[]>;
  rowActivationAction?: ActionDescriptor;
  drilldownPath?: string;
  emptyStateKey: TranslationKey;
}) {
  const { t } = useTranslation();
  const { formatByType } = useLocalizedFormatter();
  const title = t(resolveTKey(widget.titleKey));
  const unavailable = t('common:states.unavailable.message');
  const tableContract = useMemo<TableDescriptor>(
    () =>
      (widget.tableId ? getDashboardTableDescriptor(widget.tableId) : undefined) ??
      DASHBOARD_HOLDINGS_TABLE,
    [widget.tableId]
  );

  if (widget.widgetType === 'metric') {
    const currency = metricCurrency(result?.data);
    const valueFormat = widget.settings?.valueFormat;
    const value =
      result?.value === null || result?.value === undefined
        ? '\u2014'
        : valueFormat === 'currency'
          ? formatByType(result.value, 'currency', { currencyCode: currency })
          : valueFormat === 'percentage'
            ? formatByType(result.value, 'percentage')
            : valueFormat === 'number'
              ? formatByType(result.value, 'integer')
              : String(result.value);
    return (
      <MetricCard
        label={title}
        value={value}
        supportingValue={sourceAsOf}
        tone="neutral"
        stale={
          result?.value === null ||
          result?.value === undefined ||
          result?.state === 'STALE' ||
          result?.state === 'UNAVAILABLE'
        }
        to={drilldownPath}
      />
    );
  }

  if (widget.widgetType === 'task') {
    const count = Number(result?.value ?? 0);
    return (
      <MetricCard
        label={title}
        value={Number.isFinite(count) ? formatByType(count, 'integer') : result?.value ?? '\u2014'}
        supportingValue={sourceAsOf}
        tone={count > 0 ? 'warning' : 'positive'}
        stale={result?.state === 'STALE' || result?.state === 'UNAVAILABLE'}
        to={drilldownPath}
      />
    );
  }

  if (widget.widgetType === 'dataQuality') {
    return (
      <SurfaceCard
        surfaceRole={drilldownPath ? 'interactive' : 'standard'}
        padding="standard"
        component={drilldownPath ? NavLink : undefined}
        to={drilldownPath}
        aria-label={drilldownPath ? title : undefined}
        sx={{ height: '100%' }}
      >
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Alert severity={dataQualitySeverity(result?.state)}>
          {result?.value ?? unavailable}
        </Alert>
        {sourceAsOf && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            {sourceAsOf}
          </Typography>
        )}
      </SurfaceCard>
    );
  }

  if (widget.widgetType === 'table') {
    const rows =
      tableData?.[widget.id] ??
      (Array.isArray(result?.data) ? (result.data as Record<string, unknown>[]) : []);
    if (rows.length === 0) {
      return (
        <SurfaceCard surfaceRole="standard" padding="standard" sx={{ height: '100%' }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            {title}
          </Typography>
          <Alert severity="info">{t(resolveTKey(emptyStateKey))}</Alert>
        </SurfaceCard>
      );
    }
    return (
      <SurfaceCard surfaceRole="standard" padding="none" sx={{ height: '100%' }}>
        <Typography variant="h6" component="h3" sx={{ px: 2, pt: 2, pb: 1 }}>
          {title}
        </Typography>
        <TableRenderer
          contract={tableContract}
          data={rows}
          loading={false}
          presentation="embedded"
          minHeight={280}
          rowActivationAction={rowActivationAction}
          getRowId={row => {
            const value = row.id ?? row.symbol ?? row.instrumentSymbol;
            return typeof value === 'string' && value ? value : undefined;
          }}
        />
      </SurfaceCard>
    );
  }

  if (widget.widgetType === 'alert') {
    return (
      <SurfaceCard
        surfaceRole={drilldownPath ? 'interactive' : 'standard'}
        padding="standard"
        component={drilldownPath ? NavLink : undefined}
        to={drilldownPath}
        aria-label={drilldownPath ? title : undefined}
        sx={{ height: '100%' }}
      >
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Alert severity={Number(result?.value ?? 0) > 0 ? 'warning' : 'info'}>
          {result?.value ?? unavailable}
        </Alert>
      </SurfaceCard>
    );
  }

  if (widget.widgetType === 'navigation') {
    return (
      <SurfaceCard
        surfaceRole={drilldownPath ? 'interactive' : 'standard'}
        padding="standard"
        component={drilldownPath ? NavLink : undefined}
        to={drilldownPath}
        aria-label={drilldownPath ? title : undefined}
        sx={{ height: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h3">
              {title}
            </Typography>
            {result?.value && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {result.value}
              </Typography>
            )}
          </Box>
          {drilldownPath && <ChevronRightIcon color="action" aria-hidden="true" />}
        </Box>
      </SurfaceCard>
    );
  }

  if (widget.widgetType === 'chart') {
    if (!widget.visualization) {
      return (
        <SurfaceCard surfaceRole="standard" padding="standard" sx={{ height: '100%' }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            {title}
          </Typography>
          <Alert severity="error">{unavailable}</Alert>
        </SurfaceCard>
      );
    }
    const rows = Array.isArray(result?.data)
      ? result.data.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object' && !Array.isArray(row))
      : [];
    return (
      <FinancialVisualizationRenderer
        descriptor={widget.visualization}
        rows={rows}
        state={result?.state}
      />
    );
  }

  return (
    <SurfaceCard surfaceRole="standard" padding="standard" sx={{ height: '100%' }}>
      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {result?.value ?? unavailable}
      </Typography>
    </SurfaceCard>
  );
}

export function DashboardRenderer({
  contract,
  widgetResults,
  sourceAsOf,
  asOfDate,
  state,
  partialData,
  authoritativeMetadata,
  tableData,
  rowActivationActions,
  drilldownPaths,
  showTitle = true,
}: DashboardRendererProps) {
  const { t } = useTranslation();
  const { formatByType } = useLocalizedFormatter();
  const theme = useTheme();
  const supportingValue = sourceAsOf
    ? formatByType(sourceAsOf, 'datetime')
    : asOfDate
      ? formatByType(asOfDate, 'date')
      : undefined;

  const [widgetOrder, setWidgetOrder] = useState<string[]>(() => contract.widgets.map(w => w.id));
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const orderedWidgets = useMemo(() => {
    const map = new Map(contract.widgets.map(w => [w.id, w]));
    return widgetOrder.map(id => map.get(id)).filter(Boolean) as typeof contract.widgets;
  }, [contract.widgets, widgetOrder]);

  const handleDragStart = useCallback((widgetId: string) => {
    setDraggedId(widgetId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    if (widgetId !== draggedId) setDragOverId(widgetId);
  }, [draggedId]);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    setWidgetOrder(prev => {
      const fromIdx = prev.indexOf(draggedId);
      const toIdx = prev.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedId);
      return next;
    });
    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  return (
    <Box>
      {showTitle && contract.titleKey && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
          <Typography variant="h5" component="h2">
            {t(resolveTKey(contract.titleKey))}
          </Typography>
          {supportingValue && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-testid="dashboard-live-indicator">
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  animation: 'pulse-live 2s infinite',
                  display: 'inline-block',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('dashboard:liveAsOf', { date: supportingValue })}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {authoritativeMetadata ? (
        <Box sx={{ mb: 2 }}>
          <FinancialDataQualityNotice
            metadata={authoritativeMetadata}
            state={partialData ? 'PARTIAL' : state}
            sourceAsOf={sourceAsOf}
          />
        </Box>
      ) : partialData ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(resolveTKey(contract.partialStateKey))}
        </Alert>
      ) : null}
      <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
        {orderedWidgets.map(widget => {
          const position = widget.position ?? { w: 4, h: 2 };
          const width = Math.max(1, Math.min(position.w, 12));
          const height = Math.max(1, position.h);
          const result = widgetResults?.find(candidate => candidate.id === widget.id);
          const isDragging = draggedId === widget.id;
          const isDragOver = dragOverId === widget.id;
          return (
            <Grid
              size={{ xs: 12, md: width }}
              key={widget.id}
              className="dashboard-widget"
              data-testid={`dashboard-widget-${widget.id}`}
              data-widget-id={widget.id}
              data-widget-type={widget.widgetType}
              data-widget-state={result?.state ?? 'UNAVAILABLE'}
              data-widget-actionable={Boolean(drilldownPaths?.[widget.id])}
              data-widget-drilldown-path={drilldownPaths?.[widget.id]}
              data-widget-height={height}
              style={{ gridColumn: `span ${width}`, gridRow: `span ${height}` }}
              sx={{
                minWidth: 0,
                display: 'flex',
                position: 'relative',
                opacity: isDragging ? 0.5 : 1,
                outline: isDragOver ? `2px dashed ${theme.palette.primary.main}` : 'none',
                outlineOffset: 2,
                transition: 'opacity 150ms ease, outline 150ms ease',
              }}
              draggable
              onDragStart={() => handleDragStart(widget.id)}
              onDragOver={(e) => handleDragOver(e, widget.id)}
              onDrop={(e) => handleDrop(e, widget.id)}
              onDragEnd={handleDragEnd}
            >
              <IconButton
                size="small"
                aria-label={t('dashboard:dragWidget')}
                data-testid={`dashboard-widget-drag-${widget.id}`}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  zIndex: 1,
                  cursor: 'grab',
                  opacity: 0,
                  transition: 'opacity 150ms ease',
                  display: { xs: 'none', md: 'inline-flex' },
                  '.dashboard-widget:hover &': { opacity: 0.6 },
                  '&:active': { cursor: 'grabbing' },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <WidgetRenderer
                  widget={widget}
                  result={result}
                  sourceAsOf={supportingValue}
                  tableData={tableData}
                  rowActivationAction={rowActivationActions?.[widget.id]}
                  drilldownPath={drilldownPaths?.[widget.id]}
                  emptyStateKey={contract.emptyStateKey}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
